import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { getAuthErrorMessage } from '../lib/authErrors'
import AuthError from '../components/AuthError'
import type { AuthType } from '../types/auth'
import { webRedirectTarget } from '../lib/authRedirect'

function AuthCallback() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const handleAuthCallback = async () => {
      // Get the hash fragment (Supabase puts tokens there)
      const hashParams = new URLSearchParams(window.location.hash.substring(1))

      // Check for error in hash params first (Supabase error responses)
      const hashError = hashParams.get('error')
      const errorCode = hashParams.get('error_code')
      const errorDescription = hashParams.get('error_description')

      if (hashError) {
        const friendlyMessage = getAuthErrorMessage(errorCode, errorDescription)
        setError(friendlyMessage)
        return
      }

      const accessToken = hashParams.get('access_token')
      const refreshToken = hashParams.get('refresh_token')
      const type = hashParams.get('type') as AuthType | null

      // Also check query params (some flows use these)
      const tokenHash = searchParams.get('token_hash')
      const queryType = searchParams.get('type') as AuthType | null

      // If we have tokens in the hash, set the session
      if (accessToken && refreshToken) {
        const { data: sessionData, error: sessionError } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        })

        if (sessionError) {
          setError(sessionError.message)
          return
        }

        // ON-407: follow a web signup's same-origin redirect_to.
        const target = webRedirectTarget(type, searchParams.get('redirect_to'), sessionData?.user)
        if (target) {
          navigate(target, { replace: true })
          return
        }

        // Route based on auth type
        switch (type) {
          case 'recovery':
            navigate('/reset-password', { replace: true })
            break
          case 'signup':
          case 'email_change':
            navigate('/confirm-email', {
              replace: true,
              state: { type: type },
            })
            break
          case 'magiclink':
            navigate('/confirm-email', {
              replace: true,
              state: { type: 'magiclink' },
            })
            break
          default:
            navigate('/', { replace: true })
        }
        return
      }

      // Handle token_hash flow (email confirmation via OTP)
      if (tokenHash && queryType) {
        const { data: verifyData, error: verifyError } = await supabase.auth.verifyOtp({
          token_hash: tokenHash,
          type: queryType === 'signup' ? 'signup' : 'email',
        })

        if (verifyError) {
          setError(verifyError.message)
          return
        }

        const verifiedTarget = webRedirectTarget(queryType, searchParams.get('redirect_to'), verifyData?.user)
        if (verifiedTarget) {
          navigate(verifiedTarget, { replace: true })
          return
        }

        navigate('/confirm-email', {
          replace: true,
          state: { type: queryType },
        })
        return
      }

      // No valid auth params found
      setError('Invalid or expired authentication link')
    }

    handleAuthCallback()
  }, [navigate, searchParams])

  if (error) {
    return (
      <AuthError
        title="Authentication Error"
        message={error}
        details="The authentication link may have expired or already been used. Please request a new link and try again."
      />
    )
  }

  return (
    <div className="card">
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Processing authentication...</p>
      </div>
    </div>
  )
}

export default AuthCallback
